<template>
	<div class="config-container">
		<div class="right-top-bar">
			<SidebarMicro ref="micro-sidebar" :items="sidebarItems" :base-path="basePath" @switched-item="switchedTab" @switched-child-item="switchedChildTab" />
		</div>
		<div class="content">
			<div class="navbar-container">
				<TopNavbar ref="top-navbar" show-micro-sidebar :base-path="basePath" :sidebar-items="sidebarItems" @switched-item="switchedTab" @switched-child-item="switchedChildTab">
					<template #content>
						<div class="row navbar-slot-content" v-show="loaded">
							<div class="navbar-left-side">
								<h3 v-if="!userStore.mandatoryUserVerificationForBusiness() && !userStore.optionalUserVerificationForBusiness()">
									{{ topBarTitle }}
								</h3>
								<div class="verify-identity-container" v-else-if="showStripTopNavbarActions && !userStore.optionalUserVerificationForBusiness() && userStore.mandatoryUserVerificationForBusiness()">
									<TopNavbarCoverIcon cover-icon="iconWarning" :cover-value="getString('stripe_identity_mandatory_btn')"
														:loader="identityVerification" @click="openStripeIdentityVerificationSessionModal"/>
								</div>
								<div v-else-if="userStore.optionalUserVerificationForBusiness()">
									<TopNavbarCoverIcon :cover-value="getString('stripe_identity_optional_btn')"
														:loader="identityVerification" @click="openStripeIdentityVerificationSessionModal"/>
								</div>
							</div>
							<div class="navbar-right-side">
								<AppButtonIcon v-if="menuStore.itemsNum >= minMultiSelectItems && currentSidebarItem.path === '/menu'" class="multi-select-btn" @click="toggleMultiSelect(!multiSelectMode)" :active="multiSelectMode" prevent-change icon-name="icon-multiselect" />
								<AppButtonIcon v-if="currentSidebarItem.path === '/menu' || (currentSidebarItem.path === '/settings' && currentChildTab !== null && currentSidebarItem.children[currentChildTab].path === '/menu')" class="mobile-preview-btn" @click="toggleMobilePreview(true)" :active="showMobilePreview" prevent-change icon-name="icon-preview-eye" />
								<AppButton v-if="currentSidebarItem.saveButton" @click="currentSidebarItem.saveButtonCallback" :text="getString('save')" />
								<AppButtonIcon icon-name="icon-stripe" v-if="showStripTopNavbarActions" @click-icon="() => administrationStore.showStripeBottomSheet = true" :active="administrationStore.showStripeBottomSheet"/>
								<AppTabsSwitch v-if="currentSidebarItem.path === '/administration' && currentSidebarChildPath === '/taxation'" :items="taxationTabs.map(tab => getString(tab))" :active-item="taxationTabs.indexOf(activeTaxationTab)" @switched="switchTaxationTab" auto-switch />
								<AppSwitch v-if="currentSidebarItem.path === '/administration' && currentSidebarChildPath === '/cards'" fixed-color left-icon="iconTaxation" right-icon="iconPayments" :checked="cardsStore.showingCards" @switched="v => cardsStore.showingCards = v" />
							</div>
						</div>
					</template>
				</TopNavbar>
			</div>
			<div class="main-content" :class="{'no-top-padding': noTopPadding}">
				<router-view />
			</div>
		</div>
	</div>

</template>

<script>
import BusinessesSelect from "@/console-new/components/shared/BusinessesSelect.vue";
import SidebarMicro from "@/console-new/components/shared/SidebarMicro.vue";
import TopNavbar from "@/console-new/components/shared/TopNavbar.vue";
import TopNavbarCoverIcon from "@/console-new/components/shared/TopNavbarCoverIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";

import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useUserStore, useMenuStore, useRoomsStore, useMarketplaceStore, useAdministrationStore, useCardsStore} from "@/console-new/stores";
import { hasPermissionForRoute, isRouteLockedByPlan, handleLockedRoute } from "@/console-new/helpers/permissions";

import { computed } from 'vue';
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import {TAXATION_TABS, activeTaxationTab} from "@/console-new/views/config/administrations/TaxationTablesContainer.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let roomsStore = useRoomsStore();
		let notificationStore = useNotificationStore();
		let marketplaceStore = useMarketplaceStore();
		let administrationStore = useAdministrationStore();
		const svgStore = useSvgStore();
		const cardsStore = useCardsStore();
		return {getString: languageStore.getString, roomsStore, cardsStore, getIcon: svgStore.get, marketplaceStore, showNotification: notificationStore.showNotification, userStore, menuStore, administrationStore}
	},
	name: "ConfigContainer",
	components: {
		AppSwitch,
		AppTabsSwitch,
		AppList,
		ModalHeader, AppSelect, BusinessesSelect, SidebarMicro, TopNavbar, TopNavbarCoverIcon, AppButton, AppButtonIcon, AppClickableIcon, AppBottomSheet},
	data() {
		return {
			menuToSave: false,
			roomsToSave: false,
			multiSelectMode: false,
			showMobilePreview: false,
			minMultiSelectItems: 4,
			openMenu: null,
			currentTab: 0,
			currentChildTab: 0,
			loaded: false,
			basePath: "/config",
			identityVerification: false,
			taxationTabs: TAXATION_TABS,
		}
	},
	computed: {
		noTopPadding() {
			return this.$route.path === "/config/menu" || this.$route.path === "/config/administration/cards" || this.$route.path === "/config/qrcode";
		},
		plans() {
			if(this.userStore && this.userStore.currPlans) {
				return this.userStore.currPlans;
			}
			return {};
		},
		hasStripeDeadline() {
			return this.userStore.currBusiness &&
				!!this.userStore.currBusiness.stripe_account &&
				this.userStore.currBusiness.stripe_account_type === "custom" &&
				this.userStore.currBusiness.stripe_account_deadline;
		},
		hasStripeDeadlineWarning() {
			return this.hasStripeDeadline && this.userStore.currBusiness.stripe_account_deadline > new Date();
		},
		hasStripeDeadlineError() {
			return this.hasStripeDeadline && this.userStore.currBusiness.stripe_account_deadline <= new Date();
		},
		currentSidebarItem() {
			return this.sidebarItems[this.currentTab];
		},
		activeTaxationTab() {
			return activeTaxationTab(this.$route);
		},
		currentSidebarChildPath() {
			let path = null;
			if(this.sidebarItems[this.currentTab].children){
				path = this.sidebarItems[this.currentTab].children[this.currentChildTab].path;
			}
			return path;
		},
		topBarTitle() {
			let title = this.currentSidebarItem.name;
			if(this.currentSidebarItem.children && this.currentSidebarItem.children[this.currentChildTab]) {
				title += " - "+this.currentSidebarItem.children[this.currentChildTab].name;
			}
			return title;
		},
		sidebarItems() {
			return [{
				name: this.getString("products"),
				icon: this.userStore.currBusiness && this.userStore.currBusiness.type === "other" ? "iconMenuOther" : "iconMenu",
				path: "/menu",
				saveButton: true,
				saveButtonCallback: this.saveMenu,
				shown: hasPermissionForRoute('/config/menu'),
				children: null,
				class: "main-button",
			},{
				name: this.getString("qr_code_tables"),
				icon: "iconQrcode",
				path: "/qrcode",
				locked: isRouteLockedByPlan('/config/qrcode'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				saveButton: true,
				saveButtonCallback: this.saveRoomTables,
				shown: hasPermissionForRoute('/config/qrcode'),
				children: null,
				class: "main-button",
			},{
				name: this.getString("administration"),
				icon: "iconSettingsAdministration",
				path: "/administration",
				locked: isRouteLockedByPlan('/config/administration'),
				warning: this.hasStripeDeadlineWarning,
				error: this.hasStripeDeadlineError,
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				shown: hasPermissionForRoute('/config/administration'),
				children: [
					{
						name: this.getString('stripe'),
						icon: "iconStripe",
						path: "/stripe",
						warning: this.hasStripeDeadlineWarning,
						error: this.hasStripeDeadlineError
					},
					{
						name: this.getString('qromo'),
						icon: "iconQromo",
						path: "/qromo"
					},
					{
						name: this.getString('tax'),
						icon: "iconTaxation",
						path: "/taxation"
					}, {
						name: this.getString("cards"),
						icon: "iconPayments",
						path: "/cards",
					},
				],
				class: "main-button",
			}, {
				name: this.getString("settings"),
				icon: "iconManagement",
				path: "/settings",
				shown: hasPermissionForRoute('/config/settings'),
				children: [
					{
						name: this.getString("sale"),
						icon: "iconCounterSettings",
						path: "/counter",
					},
					{
						name: this.getString("generals"),
						icon: "iconSettingsGeneral",
						path: "/general",
					}
				],
				class: "main-button with-border",
			}, {
				name: this.getString("devices"),
				icon: "iconDevices",
				path: "/devices",
				shown: hasPermissionForRoute('/config/devices'),
				children: [
					{
						name: this.getString("pos"),
						icon: "iconPos",
						path: "/pos",
					}, {
						name: this.getString("printers"),
						icon: "iconPrinter",
						path: "/printers",
					}
				],
			}, {
				name: this.getString("warehouse"),
				icon: "iconWarehouse",
				shown: hasPermissionForRoute('/config/warehouse'),
				locked: isRouteLockedByPlan('/config/warehouse'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/warehouse",
				children: null,
			}]
		},
		showStripTopNavbarActions() {
			return this.currentSidebarItem.path === '/administration' && this.currentSidebarChildPath === '/stripe' && this.userStore.showStripeButton();
		}
	},
	provide() {
		return {
			providedAction: computed(() => {
				return {
					showMobilePreview: this.showMobilePreview,
					toggleMobilePreview: this.toggleMobilePreview,
					menuToSave: this.menuToSave,
					resetMenuToSave: this.resetMenuToSave,
					roomsToSave: this.roomsToSave,
					resetRoomsToSave: this.resetRoomsToSave,
					toggleMultiSelect: this.toggleMultiSelect,
					multiSelectMode: this.multiSelectMode,
				}
			})
		}
	},
	methods: {
		switchTaxationTab(index) {
			if(TAXATION_TABS[index] !== this.activeTaxationTab) {
				this.$router.replace({query: {...this.$route.query, tab: TAXATION_TABS[index]}});
			}
		},
		toggleMobilePreview(newValue = !this.showMobilePreview) {
			this.showMobilePreview = newValue;
		},
		switchedTab(index) {
			this.currentTab = index;
		},
		switchedChildTab(childIndex) {
			this.currentChildTab = childIndex;
		},
		updateCurrentTab() {
			let currentRoute = this.$route.path;
			if(currentRoute.endsWith('/administration/stripe/dashboard')) {
				currentRoute = currentRoute.replace('/dashboard', '');
			}
			this.sidebarItems.forEach((item, index) => {
				if(currentRoute.endsWith(item.path)) {
					this.currentTab = index;
				} else if(item.children) {
					item.children.forEach((child, childIndex) => {
						if(currentRoute.endsWith(item.path+child.path)) {
							this.currentTab = index;
							this.currentChildTab = childIndex;
						}
					});
				}
			});
		},
		saveRoomTables() {
			if(!this.roomsToSave) {
				this.roomsToSave = true;
			}
		},
		resetRoomsToSave() {
			this.roomsToSave = false;
		},
		saveMenu() {
			this.multiSelectMode = false;
			if(!this.menuToSave) {
				this.menuToSave = true;
			}
		},
		resetMenuToSave() {
			this.menuToSave = false;
		},
		resetOpenMenu() {
			this.openMenu = null;
		},
		toggleMultiSelect(newStatus) {
			this.menuStore.selectedItems().forEach(item => {
				item.selected = false;
			});
			this.multiSelectMode = !!newStatus;
		},
		openStripeIdentityVerificationSessionModal() {
			this.identityVerification = true;

			this.userStore.openStripeIdentityVerificationSessionModal(result => {
				this.identityVerification = false;
			});
		}
	},
	mounted() {
		this.updateCurrentTab();
		this.loaded = true;

		if(localStorage.getItem('expressDashboardNavigatedToo')) {
			this.$router.push('/config/administration/stripe');
			localStorage.removeItem('expressDashboardNavigatedToo');
		}
	},
	watch: {
		'menuStore.itemsNum'(newVal) {
			if(newVal < this.minMultiSelectItems && this.multiSelectMode) {
				this.multiSelectMode = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.config-container {
	display: flex;
	flex-direction: row;
	height: 100%;

	.right-top-bar {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		width: 250px;
		height: 100%;
		box-shadow: 0 1px 3px var(--color-alpha-3);

		.sidebar-micro {
			@include hideScrollbar;
			overflow-y: auto;
			border-top: 1px solid var(--color-alpha-3);
		}


	}

	.content {
		width: calc(100% - 250px);
	}
	.main-content {
		width: 100%;
		height: calc(100% - 60px);
		&.no-top-padding {
			padding-top: 0;
		}
	}

	.navbar-container {
		z-index: 11;
		height: 60px;
		width: 100%;

		.navbar-slot-content {
			align-items: center;
			justify-content: space-between;
			height: 100%;
			padding: 0 10px;

			@media (max-width: 920px) {
				padding: 0 10px 0 2px;
			}

			.navbar-left-side, .navbar-right-side {
				display: flex;
				align-items: center;
				justify-content: flex-start;
				height: 100%;
			}

			.navbar-left-side .verify-identity-container {
				max-width: calc(100vw - 70px);

				.cover-container:deep(.cover-value) {
					@include truncateString();
					max-width: calc(100% - 18px);
				}
			}

			.select-multi-menu {
				max-width: 150px;
				z-index: 190;

			}

			.settings-navbar-icon {
				margin-left: 4px;
				width: 15px;
				height: 15px;
				padding: 5px;
				box-sizing: content-box;

				:deep(svg) {
					.fill {
						fill: var(--color-svg-primary);
					}
					.stroke {
						stroke: var(--color-svg-primary);
					}
				}
			}
			@media (max-width: 420px) {
				.select-multi-menu {
					max-width: 120px;
					min-width: 120px;
				}
				.settings-navbar-icon {
					margin-left: 2px;
					width: 14px;
					height: 14px;
					padding: 4px;
				}

				.navbar-right-side .base-button {
					min-width: 80px;
				}
			}


			@media (max-width: 350px) {
				.select-multi-menu {
					max-width: 110px;
					min-width: 110px;
				}

				.navbar-right-side .base-button {
					min-width: 70px;
					padding: 5px 12px;
				}
			}

		}

	}

	.mobile-preview-btn {
		display: none;
		margin-left: 0;
	}

	.multi-select-btn {
		display: flex;
		margin-left: auto;
	}

	.navbar-right-side {
		gap: 12px;
	}

	@media (max-width: 1200px) {

		.right-top-bar {
			width: 220px;
		}

		.content {
			width: calc(100% - 220px);
		}
	}

	@media (max-width: 920px) {
		.navbar-container {
			width: 100%;
		}
		.right-top-bar {
			display: none;
		}
		.content {
			width: 100%;
		}
	}

	@media (max-width: 768px) {
		.main-content {
			width: 100%;
		}
		.mobile-preview-btn {
			display: flex;
		}
	}

	@media (max-width: 420px) {

		.mobile-preview-btn, .multi-select-btn {
			width: 30px;
			height: 30px;

			:deep(.btn-inner) {
				width: 25px;
				height: 25px;

				img {
					max-width: 13px;
					max-height: 13px;
				}
			}
		}

		.navbar-right-side {
			gap: 8px;
		}

	}

	@media (max-width: 350px) {

		.navbar-right-side {
			gap: 5px;
		}

	}


}
</style>
